/**
 * Translated labels and icons shared by the inbound-reply UI (TRUS-757).
 * Every key is written out literally so the i18n coverage guard can see it.
 */
import React from 'react';
import { Globe, Mail, MessageSquare, PenLine, Phone, Printer } from 'lucide-react';
import type { InboundChannel, InboundOutcomeStatus, InboundStatus } from '@/services/api/referralInbound';

export type TFn = (key: string, fallbackOrOptions?: string | Record<string, unknown>, extra?: Record<string, unknown>) => string;

export function referralStatusLabel(t: TFn, status: string | null | undefined): string {
  switch (status) {
    case 'matched': return t('referralInbound.status.matched', 'Matched');
    // provider_notified is "Sent" and service_started is "Seen" in the UI (contract).
    case 'provider_notified': return t('referralInbound.status.provider_notified', 'Sent');
    case 'provider_accepted': return t('referralInbound.status.provider_accepted', 'Accepted');
    case 'provider_declined': return t('referralInbound.status.provider_declined', 'Declined');
    case 'service_scheduled': return t('referralInbound.status.service_scheduled', 'Scheduled');
    case 'service_started': return t('referralInbound.status.service_started', 'Seen');
    case 'service_completed': return t('referralInbound.status.service_completed', 'Completed');
    case 'closed': return t('referralInbound.status.closed', 'Closed');
    case null:
    case undefined:
    case '':
      return t('referralInbound.status.none', 'No change');
    default: return status.replace(/_/g, ' ');
  }
}

export function inboundStatusLabel(t: TFn, status: InboundStatus): string {
  switch (status) {
    case 'received': return t('referralInbound.itemStatus.received', 'Received');
    case 'unmatched': return t('referralInbound.itemStatus.unmatched', 'Unmatched');
    case 'parsed': return t('referralInbound.itemStatus.parsed', 'Read');
    case 'needs_review': return t('referralInbound.itemStatus.needs_review', 'Needs review');
    case 'applied': return t('referralInbound.itemStatus.applied', 'Applied');
    case 'rejected': return t('referralInbound.itemStatus.rejected', 'Rejected');
    case 'error': return t('referralInbound.itemStatus.error', 'Error');
    default: return String(status);
  }
}

export const INBOUND_STATUS_CLASS: Record<string, string> = {
  received: 'bg-paper-2 text-ink-500',
  parsed: 'bg-paper-2 text-ink-500',
  unmatched: 'bg-attention-soft text-attention-ink',
  needs_review: 'bg-v2accent-soft text-v2accent-ink',
  applied: 'bg-success-soft text-success-ink',
  rejected: 'bg-paper-2 text-ink-400',
  error: 'bg-danger-soft text-danger-ink',
};

export function channelLabel(t: TFn, channel: InboundChannel): string {
  switch (channel) {
    case 'fax': return t('referralInbound.channel.fax', 'Fax');
    case 'call': return t('referralInbound.channel.call', 'Call');
    case 'email': return t('referralInbound.channel.email', 'Email');
    case 'sms': return t('referralInbound.channel.sms', 'Text message');
    case 'web': return t('referralInbound.channel.web', 'Web link');
    case 'manual': return t('referralInbound.channel.manual', 'Logged by staff');
    default: return String(channel);
  }
}

export function ChannelIcon({ channel, className = 'h-4 w-4' }: { channel: InboundChannel; className?: string }) {
  switch (channel) {
    case 'fax': return <Printer className={className} aria-hidden />;
    case 'call': return <Phone className={className} aria-hidden />;
    case 'email': return <Mail className={className} aria-hidden />;
    case 'sms': return <MessageSquare className={className} aria-hidden />;
    case 'web': return <Globe className={className} aria-hidden />;
    default: return <PenLine className={className} aria-hidden />;
  }
}

export function outcomeLabel(t: TFn, outcome: InboundOutcomeStatus | string): string {
  switch (outcome) {
    case 'completed': return t('referralInbound.outcome.completed', 'Service completed');
    case 'no_show': return t('referralInbound.outcome.no_show', 'No-show');
    case 'declined_service': return t('referralInbound.outcome.declined_service', 'Member declined service');
    case 'referred_elsewhere': return t('referralInbound.outcome.referred_elsewhere', 'Referred elsewhere');
    case 'other': return t('referralInbound.outcome.other', 'Other');
    default: return String(outcome).replace(/_/g, ' ');
  }
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** ISO string → value for <input type="datetime-local"> in local time. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO string (UTC). */
export function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
